/* eslint-disable react-hooks/purity */
import { ColumnChartIcon } from '@intercom/surge/icons';
import intercomLogoPath from '@/assets/svgs/intercom-logo.svg';
import { useReportSnapshot } from '@/features/report-snapshots/api/get-report-snapshot';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ReportingTable } from '@/features/report-snapshots/components/reporting-table/reporting-table';
import { ChartRenderer } from '@/features/report-snapshots/components/charts/chart-renderer';
import { FilterBar } from '@/features/report-snapshots/components/filter-bar';
import { cn } from '@intercom/surge';
import { getGridClassesWithConstraints } from '@/features/report-snapshots/utils/grid-classes';
import { getMinimumSizeConstraints } from '@/features/report-snapshots/utils/visualization-type-grid-sizes';
import { ReportSnapshotHeader } from '@/features/report-snapshots/components/report-snapshot-header';
import { useFormatDate } from '@/hooks/use-format-date';
import {
  isReportExpiring,
  isBannerChart,
  isDataTable,
  isTabularChart,
} from '@/features/report-snapshots/utils/common';
import type { ChartType } from '@/features/report-snapshots/types';
import { FetchApiError } from '@/lib/api-client';
import { captureException } from '@/utils/sentry';
import { getSchemaMode } from '@/features/report-snapshots/utils/schema-mode';

const EXPIRES_AT_THRESHOLD = 1000 * 60 * 60 * 24 * 30; // 30 days

export function ReportSnapshotWrapper() {
  const schemaMode = getSchemaMode();
  const shareToken = window.location.pathname.split('/').pop() ?? null;

  const { data, error } = useReportSnapshot(shareToken);
  const expiresDateString = data?.expiresAt
    ? new Date(data.expiresAt * 1000).toISOString()
    : new Date(Date.now() + EXPIRES_AT_THRESHOLD).toISOString();
  const expiresAt = useFormatDate(expiresDateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const isExpiring = isReportExpiring(expiresDateString);

  if (error) {
    if (error instanceof FetchApiError) {
      if (error.status === 410) {
        return <ExpiredView />;
      }

      if (error.status === 404) {
        return <SnapshotNotFoundView />;
      }
    }

    captureException(error, {
      tags: {
        feature: 'report-snapshots',
        component: 'ReportSnapshotWrapper',
        errorType: 'api-fetch',
      },
      extra: {
        status: error instanceof FetchApiError ? error.status : undefined,
        url: error instanceof FetchApiError ? error.url : undefined,
      },
      fingerprint: ['report-snapshots', 'api-fetch'],
    });

    return <SnapshotErrorView />;
  }

  if (data) {
    const chartSchemaEntriesById = Object.fromEntries(
      data.chartSchemas.map((schema) => [schema.chartId.toString(), schema]),
    );
    // Keep both tabular and legacy schemas available for each chart
    const chartDataById = Object.fromEntries(
      data.report.charts.map((chart) => {
        const chartId = String(chart.id);
        const chartSchemaEntry = chartSchemaEntriesById[chartId] as
          | (typeof chartSchemaEntriesById)[string]
          | undefined;
        const legacySchemas = chartSchemaEntry?.schemas ?? [];
        return [
          chartId,
          {
            tabularSchemas: chartSchemaEntry?.tabularSchemas ?? [],
            legacySchemas,
          },
        ];
      }),
    );

    return (
      <>
        <div className="flex flex-col gap-4 my-4 mx-6">
          <ReportSnapshotHeader
            sharedBy={data.sharedBy}
            title={data.report.title}
            description={data.report.description}
          />
          <FilterBar
            snapshotFilters={data.report.snapshotFilters}
            dateRange={data.report.dateRange}
            timezone={data.report.timezone}
          />
        </div>

        <div className="flex flex-col py-4 px-6 border-t border-dashed">
          {isExpiring && <ExpireNotice expiresAt={expiresAt} />}
          <div className="grid grid-cols-12 auto-rows-[80px] gap-y-6">
            {data.report.charts.map((chart) => {
              const visualizationType = chart.visualizationType as ChartType;
              const chartData = chartDataById[String(chart.id)] ?? {
                tabularSchemas: [],
                legacySchemas: [],
              };

              if (
                !isBannerChart(visualizationType) &&
                chartData.legacySchemas.length === 0 &&
                chartData.tabularSchemas.length === 0
              ) {
                return null;
              }

              const { minWidth, minHeight } = getMinimumSizeConstraints(visualizationType);
              const gridClasses = getGridClassesWithConstraints(
                chart.gridPositionX,
                chart.gridPositionY,
                chart.gridWidth,
                chart.gridHeight,
                minWidth,
                minHeight,
              );

              const canUseNewRenderer =
                schemaMode === 'tabular' && chartData.tabularSchemas.length > 0;

              if (isDataTable(visualizationType)) {
                return null;
              }

              if (isTabularChart(visualizationType) && !canUseNewRenderer) {
                return (
                  <div key={chart.id} className={cn(gridClasses, 'flex flex-col h-full')}>
                    <ReportingTable
                      chartSchemas={chartData.legacySchemas}
                      chartDefinition={chart}
                      timezone={data.report.timezone}
                    />
                  </div>
                );
              }

              return (
                <div
                  key={chart.id}
                  className={cn(
                    gridClasses,
                    'flex flex-col h-full',
                    isBannerChart(visualizationType) && 'relative',
                  )}
                >
                  <ChartRenderer
                    schemaMode={schemaMode}
                    visualizationType={visualizationType}
                    tabularSchemas={chartData.tabularSchemas}
                    legacySchemas={chartData.legacySchemas}
                    chartDefinition={chart}
                    timezone={data.report.timezone}
                    dateRange={data.report.dateRange}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }
}

function ExpireNotice({ expiresAt }: { expiresAt: string }) {
  return (
    <div className="flex flex-col gap-1 bg-notice-container border border-notice-border px-6 py-3 rounded-large mb-6">
      <span className="font-bold">
        <Trans>This link will expire on {expiresAt}</Trans>
      </span>
      <span>
        <Trans>After this date, you’ll no longer be able to view this report.</Trans>
      </span>
    </div>
  );
}

function ExpiredView() {
  return (
    <div className="text-center h-full bg-base-backdrop text-muted py-20">
      <ColumnChartIcon className="text-h2" />
      <h1 className="text-h2 mt-4">
        <Trans>This report is no longer available</Trans>
      </h1>
      <div className="my-2">
        <p>
          <Trans>The public link to this report has expired or access was revoked.</Trans>
        </p>
        <p>
          <Trans>You no longer have permission to view this report.</Trans>
        </p>
      </div>
      <p>
        <Trans>
          Want access again? Reach out to the person who originally shared this with you.
        </Trans>
      </p>
      <p className="mt-16 flex justify-center">
        <img src={intercomLogoPath} alt="Intercom logo" width="150px" height="24px" />
      </p>
    </div>
  );
}

function SnapshotErrorView() {
  return (
    <div className="text-center h-full bg-base-backdrop text-muted py-20">
      <ColumnChartIcon className="text-h2" />
      <h1 className="text-h2 mt-4">
        <Trans>Something went wrong</Trans>
      </h1>
      <p className="my-2">
        <Trans>We couldn&apos;t load this report.</Trans>
      </p>
      <p className="mt-16 flex justify-center">
        <img src={intercomLogoPath} alt={t`Intercom logo`} width="150px" height="24px" />
      </p>
    </div>
  );
}

function SnapshotNotFoundView() {
  return (
    <div className="text-center h-full bg-base-backdrop text-muted py-20">
      <p>
        <Trans>The requested report does not exist. Please check your link and try again.</Trans>
      </p>
      <p className="mt-16 flex justify-center">
        <img src={intercomLogoPath} alt="Intercom logo" width="150px" height="24px" />
      </p>
    </div>
  );
}
